<template>
  <section class="jql-panel" data-test="jql-panel">
    <header class="jql-head">
      <h3 class="jql-title">{{ title }}</h3>
      <span class="jql-sub">Open the matching ticket list in Jira</span>
    </header>
    <ul class="jql-list">
      <li v-for="p in presets" :key="p.id" class="jql-row" :title="p.description">
        <a
          :href="p.url"
          target="_blank"
          rel="noopener noreferrer"
          class="jql-link"
          :data-test="`jql-${p.id}`"
        >{{ p.label }}</a>
        <span
          v-if="counts && countOf(p.id) !== null"
          class="jql-count"
          :class="countTone(p.id)"
          :data-test="`jql-${p.id}-count`"
        >{{ countOf(p.id) }}</span>
        <span v-else-if="counts" class="jql-count jql-count-na" :title="'Count unavailable'">—</span>
        <code class="jql-text" :title="p.jql">{{ p.jql }}</code>
        <a
          :href="p.url"
          target="_blank"
          rel="noopener noreferrer"
          class="jql-arrow-link"
          aria-label="Open in Jira"
          tabindex="-1"
        >↗</a>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  projectPresets,
  sprintPresets,
  type JqlFilterPreset,
} from "@/lib/jiraJql";

const props = defineProps<{
  projectKey: string;
  sprintId?: number;
  sprintName?: string;
  /** "project" presets are project-wide; "sprint" presets are scoped to a single sprint. */
  scope?: "project" | "sprint";
  /** Kanban window: scope presets by `updated >= -Nd` instead of `sprint = <id>`. */
  windowDays?: number;
  /** Optional per-preset count (keyed by preset id, e.g. "sprint-missing-sp"). */
  counts?: Record<string, number | null>;
}>();

const presets = computed<JqlFilterPreset[]>(() => {
  if (props.scope === "sprint" && props.sprintId != null) {
    return sprintPresets({
      projectKey: props.projectKey,
      sprintId: props.sprintId,
      sprintName: props.sprintName,
      windowDays: props.windowDays,
    });
  }
  return projectPresets({ projectKey: props.projectKey });
});

const title = computed(() => {
  if (props.scope !== "sprint") return "Quick filters for this project";
  return props.windowDays != null ? "Quick filters for this window" : "Quick filters for this sprint";
});

function countOf(id: string): number | null {
  const v = props.counts?.[id];
  return typeof v === "number" ? v : null;
}

function countTone(id: string): string {
  const v = countOf(id);
  if (v === null) return "";
  if (v === 0) return "tone-ok";
  // Only the "missing-*" rule counts can spike to red; the navigation
  // rows (all/open/mine) get amber so we don't double-flag.
  if (id.includes("missing")) return v >= 5 ? "tone-bad" : "tone-warn";
  return "tone-warn";
}
</script>

<style scoped>
.jql-panel {
  display: flex; flex-direction: column; gap: 8px;
  padding: 14px 16px;
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 8px;
}
.jql-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.jql-title { margin: 0; font-size: 14px; font-weight: 600; color: var(--text-color, #111827); }
.jql-sub { font-size: 11px; color: var(--text-muted, #6b7280); text-transform: uppercase; letter-spacing: 0.04em; }

.jql-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; }
.jql-row {
  display: grid;
  grid-template-columns: minmax(140px, max-content) auto 1fr auto;
  gap: 10px;
  padding: 6px 10px;
  border: 1px solid var(--surface-border, #f1f5f9);
  border-radius: 6px;
  align-items: center;
  min-width: 0;
}
.jql-row:hover { background: var(--surface-elev, #f9fafb); }
.jql-link {
  font-weight: 600; font-size: 13px;
  color: #2563eb; text-decoration: none;
  white-space: nowrap;
}
.jql-link:hover { text-decoration: underline; }
.jql-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: var(--text-muted, #4b5563);
  background: var(--surface-elev, #f3f4f6);
  padding: 2px 6px; border-radius: 4px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  min-width: 0;
}
.jql-arrow-link {
  font-size: 13px; color: var(--text-muted, #6b7280); text-decoration: none;
  padding: 0 2px;
}
.jql-arrow-link:hover { color: #2563eb; }

.jql-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 26px; height: 20px; padding: 0 8px;
  font-size: 11px; font-weight: 600;
  border-radius: 999px;
  background: var(--surface-elev, #f3f4f6);
  color: var(--text-muted, #4b5563);
  border: 1px solid var(--surface-border, #e5e7eb);
}
.jql-count.tone-ok   { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.jql-count.tone-warn { background: #fffbeb; color: #92400e; border-color: #fcd34d; }
.jql-count.tone-bad  { background: #fef2f2; color: #991b1b; border-color: #fca5a5; }
.jql-count.jql-count-na { opacity: 0.5; }
</style>
